/**
 * Underneath Match ("Wall-Read on #2") as a responsibility spec — the apex/slot defender.
 *
 * The INVERSE of the corner's man-match read. Where a corner mans a key who gets vertical and
 * passes off a short break, the apex jumps a short break and passes off a vertical: the corner
 * and safety have anything deep, so the apex's job is everything underneath.
 *
 * Used by DB/LB1 (keys WR2) and DB/LB2 (keys WR3), which differ only in `outSignX` and the side
 * of the curl-flat landmark. See `.kiro/steering/cover4Match.md`.
 */

import { UnderneathMatchParams } from '../../types';
import { isHardBreak, isSettled, isComingBackToLos, isBreakingToward, landmarkFor } from '../observables';
import type { KeyObservables } from '../observables';
import { ResponsibilitySpec } from '../responsibility';

/** The depth at which THIS key, still on his stem, is a vertical threat to be passed off. */
const verticalLineOf = (key: KeyObservables, p: UnderneathMatchParams) =>
  landmarkFor(key, p.verticalDepthYards) + p.breakDepthToleranceY;

/** Did the key break at all this tick — a hard cut or a settle? */
const broke = (key: Parameters<typeof isHardBreak>[0], p: UnderneathMatchParams) =>
  isHardBreak(key, p.breakAngleDeg) || isSettled(key, p.settleSpeedFraction);

/** Partner slots, in the order the resolver supplies them. */
const KEY1 = 0;
const RB = 1;

/**
 * #1 working SHORT INSIDE into this apex's area — the "reroute #2, cover #1" trigger. A slant or
 * drag cutting toward the middle while still shallow. Gated on the SHORT line, not just the deep
 * line: a Dig breaks in at ~13 yds and a Post climbs from its break, so both sit below this
 * ceiling and stay the corner's / safety's. Inert unless the params carry the `key1*` config AND
 * a #1 feed was supplied as the partner. Because the enclosing latch is sticky, once it fires
 * while #1 is shallow the apex stays committed across the field.
 */
const key1ShortIn = (obs: KeyObservables | undefined, p: UnderneathMatchParams) =>
  !!obs &&
  p.key1OutSignX !== undefined &&
  p.key1DeepLineYards !== undefined &&
  p.key1ShortLineYards !== undefined &&
  obs.position.y > landmarkFor(obs, p.key1DeepLineYards) && // not gone deep
  obs.position.y > landmarkFor(obs, p.key1ShortLineYards) && // still shallow (a Dig/Post break deeper)
  isHardBreak(obs, p.breakAngleDeg) &&
  isBreakingToward(obs, -p.key1OutSignX, p.lateralEps); // breaking IN (opposite the out sign)

/**
 * The back releasing fast and wide to THIS apex's flat while still shallow — the observable that
 * triggers a "Push" (the hook can't reach him, so the apex takes him). Inert unless the params
 * carry the `rb*` push config AND an RB feed was supplied as the partner. Mirrors the apex read's
 * `rbToMySide`. Because the enclosing latch is sticky, once this fires while the back is shallow
 * the apex stays committed to him even after he turns vertical up the sideline.
 */
const rbPushToMySide = (obs: KeyObservables | undefined, p: UnderneathMatchParams) =>
  !!obs &&
  p.rbStartX !== undefined &&
  p.rbFlatSignX !== undefined &&
  p.rbFlatThresholdX !== undefined &&
  p.rbFlatDepthY !== undefined &&
  (obs.position.x - p.rbStartX) * p.rbFlatSignX > p.rbFlatThresholdX &&
  obs.position.y > p.rbFlatDepthY;

export const UNDERNEATH_MATCH_SPEC: ResponsibilitySpec<UnderneathMatchParams> = {
  id: 'wall-read',
  initialTechnique: 'backpedal',

  latches: {
    /** The back pushed to my flat — sticky, so the commitment survives him turning up the field. */
    rbPush: ({ partners, params }) => rbPushToMySide(partners[RB], params),
    /** #1 cut short inside my area — sticky, so the commitment survives him crossing the field. */
    key1Cross: ({ partners, params }) => key1ShortIn(partners[KEY1], params),
  },

  bindings: {
    /**
     * Whom he came off #2 to cover, if anyone. A loose back on a Push outranks everything and can
     * pull him even out of a manned #2. A #1 crosser only binds while he is NOT already manning #2
     * (state !== 'MAN'): a #2 short out/hitch he jumped is his, and the #1 in-breaker must not
     * steal him off it — the doc's "cover #1 ... and no player in flats OR short out". The
     * interpreter only sets a binding whose index is a real partner, so with no RB/#1 feed (e.g. a
     * Cover 6 apex) this stays null and the wall-read is unchanged.
     */
    covering: ({ latches, partners, state }) => {
      if (latches.rbPush && partners[RB]) return RB;
      if (state === 'ZONE' && latches.key1Cross && partners[KEY1]) return KEY1;
      return null;
    },
  },

  transitions: [
    {
      // Ordered FIRST: a Push outranks every #2 read. The back is loose in this apex's flat and
      // the hook has pushed him off; the apex leaves #2 (corner/safety/hook have him) and mans the
      // back from wherever his drop carried him — even out of an already-manned #2.
      note: 'Push — the back released fast to my flat, come off #2 and man #3',
      from: ['READ', 'MAN', 'ZONE'],
      to: 'MAN',
      when: ({ latches, partners }) => latches.rbPush && !!partners[RB],
    },
    {
      // Ordered first among the #2 reads: reaching the vertical landmark decides the read
      // regardless of what else happened this tick. He is a vertical threat, deep defenders own him.
      note: 'reached the vertical landmark on his stem — pass him off, sink to the flat',
      from: ['READ'],
      to: 'ZONE',
      when: ({ key, params }) => key.position.y <= verticalLineOf(key, params),
    },
    {
      note: 'hitch or settle back to the QB — jump it',
      from: ['READ'],
      to: 'MAN',
      when: ({ key, params }) =>
        broke(key, params) &&
        (isComingBackToLos(key, params.backToLosSpeed) || isSettled(key, params.settleSpeedFraction)),
    },
    {
      // A short out toward the sideline. Normally the apex jumps it — but in Cover 4 Palms the
      // CORNER makes the "Out" call and takes #2, so the apex passes it off and sinks instead.
      // `passOffOutBreak` is how that derived call reaches this defender: both he and the corner
      // observe the same #2 release and each draws his own conclusion.
      note: 'short out toward the sideline',
      from: ['READ'],
      to: 'MAN',
      when: ({ key, params }) =>
        !params.passOffOutBreak &&
        broke(key, params) &&
        isBreakingToward(key, params.outSignX, params.lateralEps),
    },
    {
      note: 'short in-breaker (slant/drag), or an out the corner is taking — pass off, expand to the flat',
      from: ['READ'],
      to: 'ZONE',
      when: ({ key, params }) => broke(key, params),
    },
    {
      // Ordered LAST, and only from ZONE — after #2 has been passed off (he went vertical or
      // short-inside). A #2 the apex is manning (a short out or hitch he jumped) is HIS and keeps
      // him — the doc's "cover #1 ... and no player in flats OR short out". Only once #2 is gone
      // does the apex reroute him and carry the #1 short in-breaker across the field. The latch is
      // sticky, so a #1 who broke in early is still taken the moment #2 clears.
      note: '#1 short in-breaker after #2 passed off — reroute #2, cover #1',
      from: ['ZONE'],
      to: 'MAN',
      when: ({ latches, partners }) => latches.key1Cross && !!partners[KEY1],
    },
  ],

  behavior: {
    // Mirror the key laterally while holding underneath depth — he walls, he does not chase.
    READ: ({ key, params, defender }) => ({
      target: { x: key.position.x, y: defender.y },
      speed: params.readSpeed,
    }),

    // Sit just underneath and trail — the back if he pushed to this apex on a Push call, else
    // the #2 he jumped. Trailing the bound partner's LIVE position is what carries the back up a
    // wheel: the depth gate only governs WHEN he commits (the sticky latch), never whom he trails.
    MAN: (ctx) => {
      const target = ctx.bound('covering') ?? ctx.key;
      return {
        target: { x: target.position.x, y: target.position.y + ctx.params.trailCushion },
        speed: ctx.params.maxSpeed,
      };
    },

    // Expand to the curl-flat and settle, reading for anyone in the flat.
    ZONE: ({ params }) => ({ target: params.zoneLandmark, speed: params.zoneDropSpeed }),
  },
};
